import { TbQuote } from "solid-icons/tb";
import { type Component, createSignal, onCleanup, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { onButtonKeyDown } from "../buttons/buttonKeyDown";

/**
 * Displays a quoted text reference in chat message history.
 * Read-only component styled to match PromptBarQuote with hover expansion.
 */
export const QuoteDisplay: Component<{ quote: string }> = (props) => {
  const [popOut, setPopOut] = createSignal(false);

  let hoverTimeout: NodeJS.Timeout | undefined;
  let focusTimeout: NodeJS.Timeout | undefined;

  const handleHoverStart = () => {
    hoverTimeout = setTimeout(() => {
      setPopOut(true);
    }, 500);
  };

  const handleHoverEnd = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = undefined;
    }
    setPopOut(false);
  };

  const handleFocus = () => {
    focusTimeout = setTimeout(() => {
      setPopOut(true);
    }, 500);
  };

  const handleBlur = () => {
    if (focusTimeout) {
      clearTimeout(focusTimeout);
      focusTimeout = undefined;
    }
    setPopOut(false);
  };

  const togglePopOut = () => {
    clearTimeout(focusTimeout);
    setPopOut(!popOut());
  };

  onCleanup(() => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    if (focusTimeout) clearTimeout(focusTimeout);
  });

  return (
    <div class="relative mb-0 pt-1">
      <div
        role="button"
        tabindex={0}
        aria-expanded={popOut()}
        class="relative flex animate-fade-in cursor-pointer items-start gap-2 rounded-t-[1.2rem] bg-inset px-6 py-2 pt-3 transition-all select-none"
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleHoverEnd}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onButtonKeyDown(togglePopOut)}
      >
        <TbQuote class="shrink-0 text-disabled-text" />
        <p
          class={twMerge(
            "h-full max-h-[2.9rem] flex-1 overflow-y-hidden text-left text-xs whitespace-pre-wrap transition-all",
            popOut() && "max-h-[6.6rem] overflow-y-auto"
          )}
        >
          {props.quote}
        </p>
        <Show when={!popOut()}>
          <div class="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-[1.2rem] bg-linear-to-b from-transparent to-inset" />
        </Show>
      </div>
    </div>
  );
};
